import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurl } from "../../config/url";
import {
  FiSearch,
  FiFileText,
  FiAlertCircle,
  FiDownload,
  FiRefreshCw,
  FiX,
  FiClock,
  FiHash,
  FiDollarSign,
} from "react-icons/fi";

const BillingHistory = () => {
  const navigate = useNavigate();
  const [billingData, setBillingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI-only state
  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetchBillingHistory = async () => {
      try {
        const token = localStorage.getItem("token");
        const sellerId = localStorage.getItem("sellerId");

        if (!token || !sellerId) {
          console.error("No token or seller ID available. Redirecting to login...");
          navigate("/login");
          return;
        }

        const response = await axios.get(
          `${baseurl}/api/payment/billing-history/${sellerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("Full API Response:", response.data);
        console.log("Payments Data:", response.data.payments);

        setBillingData(response.data.payments);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching billing history:", error);
        setError("Failed to fetch billing history");
        setLoading(false);
      }
    };

    fetchBillingHistory();
  }, [navigate]);

  const handleDetails = (payment) => {
    navigate("/invoice-detail", { state: { payment } });
  };

  // UI-only: local filter (does not touch API / logic)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return billingData;

    return billingData.filter((p) => {
      const id = (p.orderCreationId || p.orderId || "").toLowerCase();
      const type = (p.paymentType || "").toLowerCase();
      const currency = (p.currency || "").toLowerCase();
      const amount = String(p.amount ?? "").toLowerCase();
      const date = p.createdAt
        ? new Date(p.createdAt).toLocaleDateString().toLowerCase()
        : "";
      return (
        id.includes(q) ||
        type.includes(q) ||
        currency.includes(q) ||
        amount.includes(q) ||
        date.includes(q)
      );
    });
  }, [billingData, query]);

  const totalPaid = useMemo(() => {
    return (billingData || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
  }, [billingData]);

  const txCount = billingData?.length || 0;

  if (loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-slate-50 px-4">
        <div className="w-full max-w-3xl bg-white border rounded-3xl p-6 sm:p-7 shadow-sm">
          <div className="animate-pulse space-y-4">
            <div className="h-7 w-52 bg-slate-200 rounded" />
            <div className="h-4 w-80 bg-slate-200 rounded" />
            <div className="grid grid-cols-2 gap-3">
              <div className="h-20 bg-slate-200 rounded-3xl" />
              <div className="h-20 bg-slate-200 rounded-3xl" />
            </div>
            <div className="h-11 w-full bg-slate-200 rounded-2xl" />
            <div className="h-64 w-full bg-slate-200 rounded-3xl" />
          </div>
          <p className="text-sm text-slate-500 mt-4">Loading billing history…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-slate-50 px-4">
        <div className="w-full max-w-2xl bg-white border rounded-3xl p-6 sm:p-7 shadow-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-50 border border-red-100 px-3 py-1 text-red-700 font-extrabold text-sm">
            <FiAlertCircle />
            Error
          </div>

          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-3">
            Unable to load billing history
          </h2>
          <p className="text-slate-600 mt-2">{error}</p>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-2xl bg-slate-900 text-white font-extrabold hover:bg-slate-800 transition"
            >
              <FiRefreshCw />
              Refresh
            </button>

            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center justify-center h-11 px-5 rounded-2xl border bg-white text-slate-900 font-extrabold hover:bg-slate-50 transition"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Billing History
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Review all your past billing transactions at a glance.
            </p>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-3 w-full sm:w-auto">
            <div className="rounded-3xl border bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-extrabold text-slate-500">
                  Transactions
                </p>
                <FiHash className="text-slate-400" />
              </div>
              <p className="text-lg font-extrabold text-slate-900">{txCount}</p>
            </div>

            <div className="rounded-3xl border bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-extrabold text-slate-500">
                  Total Amount
                </p>
                <FiDollarSign className="text-slate-400" />
              </div>
              <p className="text-lg font-extrabold text-slate-900">
                ₹{Number(totalPaid || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Search + Actions */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-5">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <FiSearch />
              </span>

              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by Order ID, Type, Date, Amount…"
                className="w-full h-11 pl-10 pr-12 rounded-2xl border bg-white text-slate-900 placeholder:text-slate-400
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {query?.trim() ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-xl
                             grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
                  title="Clear"
                  aria-label="Clear search"
                >
                  <FiX />
                </button>
              ) : null}
            </div>

            {/* UI-only buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-2xl border bg-white text-slate-900 font-extrabold
                           hover:bg-slate-50 transition"
                title="Print / Save as PDF"
              >
                <FiDownload />
                Export
              </button>

              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-2xl bg-slate-900 text-white font-extrabold
                           hover:bg-slate-800 transition"
                title="Refresh"
              >
                <FiRefreshCw />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Small status line */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-xs text-slate-500 inline-flex items-center gap-2">
              <FiClock className="text-slate-400" />
              Showing{" "}
              <span className="font-extrabold text-slate-700">{filtered.length}</span>{" "}
              of{" "}
              <span className="font-extrabold text-slate-700">{billingData.length}</span>{" "}
              transactions
            </p>

            {query?.trim() ? (
              <span className="text-xs font-extrabold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 w-fit">
                Filter active
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white border rounded-3xl shadow-sm overflow-hidden">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead className="bg-slate-50">
                <tr>
                  <th className="py-3 px-5 sm:px-6 text-left text-[11px] font-extrabold tracking-wide text-slate-600">
                    Order ID
                  </th>
                  <th className="py-3 px-5 sm:px-6 text-left text-[11px] font-extrabold tracking-wide text-slate-600">
                    Date
                  </th>
                  <th className="py-3 px-5 sm:px-6 text-left text-[11px] font-extrabold tracking-wide text-slate-600">
                    Type
                  </th>
                  <th className="py-3 px-5 sm:px-6 text-left text-[11px] font-extrabold tracking-wide text-slate-600">
                    Amount
                  </th>
                  <th className="py-3 px-5 sm:px-6 text-right text-[11px] font-extrabold tracking-wide text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((payment, index) => {
                  const orderId = payment.orderCreationId || payment.orderId || "—";
                  const createdAt = payment.createdAt
                    ? new Date(payment.createdAt).toLocaleDateString()
                    : "—";
                  const paymentType = payment.paymentType || "—";
                  const currency = payment.currency || "₹";
                  const amount = payment.amount ?? "—";

                  return (
                    <tr
                      key={index}
                      className={`border-t ${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                      } hover:bg-blue-50/40 transition-colors`}
                    >
                      <td className="py-4 px-5 sm:px-6">
                        <p className="text-sm font-extrabold text-slate-900">
                          {orderId}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Ref: {String(orderId).slice(0, 14)}…
                        </p>
                      </td>

                      <td className="py-4 px-5 sm:px-6 text-sm text-slate-700 font-semibold">
                        {createdAt}
                      </td>

                      <td className="py-4 px-5 sm:px-6">
                        <span className="inline-flex items-center rounded-full border bg-white px-3 py-1 text-xs font-extrabold text-slate-700">
                          {paymentType}
                        </span>
                      </td>

                      <td className="py-4 px-5 sm:px-6">
                        <p className="text-sm font-extrabold text-slate-900">
                          {currency} {amount}
                        </p>
                      </td>

                      <td className="py-4 px-5 sm:px-6 text-right">
                        <button
                          onClick={() => handleDetails(payment)}
                          className="inline-flex items-center gap-2 h-10 px-4 rounded-2xl bg-blue-600 text-white font-extrabold
                                     hover:bg-blue-700 transition shadow-sm"
                        >
                          <FiFileText />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-12 px-5 sm:px-6 text-center">
                      <div className="mx-auto max-w-md">
                        <p className="text-sm font-extrabold text-slate-900">
                          No results found
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Try searching with Order ID, payment type, date, currency, or amount.
                        </p>
                        <button
                          type="button"
                          onClick={() => setQuery("")}
                          className="mt-4 inline-flex items-center justify-center h-10 px-4 rounded-2xl border bg-white
                                     text-slate-900 font-extrabold hover:bg-slate-50 transition"
                        >
                          Clear search
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Tip */}
          <div className="px-5 sm:px-6 py-4 border-t bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-xs text-slate-500">
              Tip: Click <span className="font-extrabold text-slate-700">View</span>{" "}
              to open the invoice details for a transaction.
            </p>
            <p className="text-xs text-slate-400">
              Export uses your browser Print / Save as PDF.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;